// CST207 Step 9: Locale-aware formatting utilities.
// All formatters derive the locale from i18n.language so they respond to
// language switches without prop-drilling.

import { i18n, SUPPORTED_LNGS } from "@/i18n/config";

/** Map i18n language codes to BCP 47 locale tags for Intl APIs. */
const LOCALE_MAP = {
  de: "de-DE",
  en: "en-GB",
} as const satisfies Record<(typeof SUPPORTED_LNGS)[number], string>;

function getLocale(): string {
  const lng = i18n.language ?? "de";
  return LOCALE_MAP[lng as keyof typeof LOCALE_MAP] ?? "de-DE";
}

/**
 * Cache Intl formatter instances per locale+options combo.
 * Avoids creating ~1300 throwaway Intl objects per admin table render.
 */
const formatterCache = new Map<
  string,
  Intl.DateTimeFormat | Intl.NumberFormat
>();

function getCachedDateTimeFormat(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `dt:${locale}:${JSON.stringify(options)}`;
  let fmt = formatterCache.get(key) as Intl.DateTimeFormat | undefined;
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(key, fmt);
  }
  return fmt;
}

function getCachedNumberFormat(
  locale: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const key = `nf:${locale}:${JSON.stringify(options)}`;
  let fmt = formatterCache.get(key) as Intl.NumberFormat | undefined;
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, fmt);
  }
  return fmt;
}

// Invalidate cache on language change to keep it bounded.
i18n.on("languageChanged", () => {
  formatterCache.clear();
});

/** Format an ISO timestamp as a short date+time string (e.g. "10.04.2026, 14:30"). */
export function formatTimestamp(iso: string): string {
  const locale = getLocale();
  const opts: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  return getCachedDateTimeFormat(locale, opts).format(new Date(iso));
}

/** Format an ISO timestamp as a full date+time string (e.g. "10.04.2026, 14:30"). */
export function formatDateTime(iso: string): string {
  const locale = getLocale();
  const opts: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return getCachedDateTimeFormat(locale, opts).format(new Date(iso));
}

/** Format an ISO timestamp as a human-readable date (e.g. "10. April 2026, 14:30"). */
export function formatScrapedAt(iso: string): string {
  const locale = getLocale();
  const opts: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return getCachedDateTimeFormat(locale, opts).format(new Date(iso));
}

/** Format an ISO timestamp as compact date (e.g. "10. Apr. 2026, 14:30"). */
export function formatShortDate(iso: string): string {
  const locale = getLocale();
  const opts: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return getCachedDateTimeFormat(locale, opts).format(new Date(iso));
}

/** Format an ISO timestamp as compact date without year (e.g. "10. Apr., 14:30"). */
export function formatShortDateNoYear(iso: string): string {
  const locale = getLocale();
  const opts: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  };
  return getCachedDateTimeFormat(locale, opts).format(new Date(iso));
}

/** Format a currency amount from cents (e.g. 4900 → "49,00 €"). */
export function formatPrice(priceCents: number, currency: string): string {
  const locale = getLocale();
  const opts: Intl.NumberFormatOptions = {
    style: "currency",
    currency: currency.toUpperCase(),
  };
  return getCachedNumberFormat(locale, opts).format(priceCents / 100);
}

/** Format a currency amount (already in major units, e.g. 49.00 → "49,00 €"). */
export function formatCurrency(value: number, currency = "EUR"): string {
  const locale = getLocale();
  const opts: Intl.NumberFormatOptions = {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  };
  return getCachedNumberFormat(locale, opts).format(value);
}

/** Format a plain number with locale grouping (e.g. 1234 → "1.234"). */
export function formatNumber(value: number): string {
  const locale = getLocale();
  return getCachedNumberFormat(locale, {}).format(value);
}
